import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import React, { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { AppContext } from '../common/AppContext';
import { TableSearchDebounceTime } from '../constants';

interface FilterRef {
  categories: any[];
  expiry: string | boolean | null;
  training: string | boolean | null;
}

interface RouteState {
  searchText: string;
  queryText: string | null;
  filters: Record<string, any>;
  filterRef: FilterRef;
  pagingModel: GridPaginationModel;
  sortModel: GridSortModel;
}

interface StandardsLibraryContextType {
  jobs: RouteState;
  tasks: RouteState;
  competencies: RouteState;
  isTrial: boolean;
  setRouteState: (route: 'jobs' | 'tasks' | 'competencies', updates: Partial<RouteState>) => void;
  debounceSearch: (route: 'jobs' | 'tasks' | 'competencies', value: string) => void;
}

const StandardsLibraryContext = createContext<StandardsLibraryContextType | undefined>(undefined);

const initialRouteState: RouteState = {
  searchText: '',
  queryText: null,
  filters: {},
  filterRef: {
    categories: [],
    expiry: null,
    training: null,
  },
  pagingModel: { page: 0, pageSize: 25 },
  sortModel: [],
};

export const StandardsLibraryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [jobs, setJobs] = useState<RouteState>(initialRouteState);
  const [tasks, setTasks] = useState<RouteState>(initialRouteState);
  const [competencies, setCompetencies] = useState<RouteState>(initialRouteState);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { organization, user } = useContext(AppContext);

  const isTrial = Boolean(
    !user?.isFullAccess ||
    organization?.isTrial ||
    !organization?.enabledFeatures?.KlayoIntelligence
  );

  const setRouteState = (
    route: 'jobs' | 'tasks' | 'competencies',
    updates: Partial<RouteState>
  ) => {
    switch (route) {
      case 'jobs':
        setJobs((prev) => ({ ...prev, ...updates }));
        break;
      case 'tasks':
        setTasks((prev) => ({ ...prev, ...updates }));
        break;
      case 'competencies':
        setCompetencies((prev) => ({ ...prev, ...updates }));
        break;
    }
  };
  const debounceSearch = useCallback(
    (route: 'jobs' | 'tasks' | 'competencies', value: string) => {
      setRouteState(route, { searchText: value });

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        // Get current pageSize before updating
        let currentState;
        switch (route) {
          case 'jobs':
            currentState = jobs;
            break;
          case 'tasks':
            currentState = tasks;
            break;
          case 'competencies':
            currentState = competencies;
            break;
        }

        setRouteState(route, {
          queryText: value,
          pagingModel: {
            page: 0,
            pageSize: currentState.pagingModel.pageSize,
          },
        });
      }, TableSearchDebounceTime);
    },
    [jobs, tasks, competencies]
  );

  return (
    <StandardsLibraryContext.Provider
      value={{
        jobs,
        tasks,
        competencies,
        isTrial,
        setRouteState,
        debounceSearch,
      }}
    >
      {children}
    </StandardsLibraryContext.Provider>
  );
};

export const useStandardsLibrary = () => {
  const context = useContext(StandardsLibraryContext);
  if (context === undefined) {
    throw new Error('useStandardsLibrary must be used within a StandardsLibraryProvider');
  }
  return context;
};
